import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { appSettings } from '@app/shared/app-settings';

import { AuthenticatedUser } from '@app/domain/model/authenticated-user';
import { Authentication } from '@app/domain/model/authentication';
import { ResetPasswordParams } from '@app/domain/model/reset-password-params';
import { ToastrService } from 'ngx-toastr';
import { CookieService } from 'ngx-cookie-service';

const COOKIE_NAME = 'currentUser';

/**
 * Dealer authentication service.
 *
 * @author Warlock, AIS.PL
 */
@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  constructor(private http: HttpClient,
              private readonly toastrService: ToastrService,
              private cookieService: CookieService) {
  }

  getCurrentUser(): AuthenticatedUser {
    const jsonString = this.cookieService.get(COOKIE_NAME);
    return jsonString ? AuthenticatedUser.fromJSON(JSON.parse(jsonString)) : null;
  }

  isLoggedIn(): boolean {
    const currentUser = this.cookieService.get(COOKIE_NAME);
    return null != currentUser && '' !== currentUser;
  }

  /**
   * Performs user authentication.
   *
   * This method sends appropriate 'POST' request to REST API authentication end-point, and converts the returned
   * information about the authenticated user into {@link AuthenticatedUser} instance.
   *
   * If REST will respond with 'Bad Request' (400) error, we do assume that authentication was unsuccessful,
   * and return null to the caller, to let him know about it.
   *
   * @param userCredentials user's credentials
   */
  login(userCredentials: Authentication): Observable<AuthenticatedUser> {
    const formData = new FormData();

    formData.append('username', userCredentials.username);
    formData.append('password', userCredentials.password);

    return this.http
      .post('login', formData)
      .pipe(
        map(response => {
          let user: AuthenticatedUser;
          if (response) {
            user = AuthenticatedUser.fromJSON(response);
            this.cookieService.set(COOKIE_NAME, JSON.stringify(user));
          }
          return user;
        }), catchError(this.handleError));
  }

  logout() {
    return this.http
      .get('logout')
      .toPromise()
      .then(response => this.clearSession());
  }

  resetPassword(resetPasswordParams: ResetPasswordParams) {
    const httpParams = new HttpParams()
      .set('licenseId', resetPasswordParams.licenseId.toString());

    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
    };
    return this.http.post(appSettings.resetPasswordURL, httpParams, options);
  }

  resetMyPassword() {
    return this.resetPassword({ licenseId: this.getCurrentUser().dealerId });
  }

  clearSession() {
    this.cookieService.delete(COOKIE_NAME);
  }

  private handleError(error: any): Promise<AuthenticatedUser> {
    if (isDevMode()) {
      console.error('Authentication error: ', error);
    }
    if ((error instanceof HttpErrorResponse) && (error.status === 400)) {
      return Promise.resolve(null);
    }
    return Promise.reject(error.message || error);
  }

}
